import { useEffect, useState, useCallback } from "react";
import { useParams, Link, useSearchParams, useNavigate } from "react-router-dom";
import { storePath } from "../lib/storeUrl";
import { ArrowLeft, UserRound, LogOut, Package, Check, Pencil, X } from "lucide-react";
import { toast } from "sonner";
import api, { fmtINR, fmtDateTime, errMsg, fileUrl } from "../lib/api";
import { themeVars, headingFont, SafeImg } from "../components/StoreKit";
import { StoreHeader, StoreFooter } from "../components/store/StoreChrome";
import { getCustomerToken, setCustomerSession, clearCustomerSession } from "../lib/customer";
import RESite from "./realestate/RESite";

const TRACK = [["pending", "Order Placed"], ["confirmed", "Confirmed"], ["shipped", "Shipped"], ["delivered", "Delivered"]];

const TrackBar = ({ status }) => {
  if (status === "cancelled") return <p className="text-xs font-extrabold mt-3" style={{ color: "#dc2626" }} data-testid="order-tracking">Order cancelled</p>;
  const idx = Math.max(0, TRACK.findIndex(([k]) => k === status));
  return (
    <div className="flex items-start mt-4" data-testid="order-tracking">
      {TRACK.map(([k, label], i) => (
        <div key={k} className="flex items-center flex-1 last:flex-none">
          <div className="flex flex-col items-center gap-1">
            <div className="h-6 w-6 rounded-full flex items-center justify-center text-[10px] font-extrabold"
              style={i <= idx ? { backgroundColor: "var(--sp)", color: "#fff" } : { backgroundColor: "var(--ss)", color: "var(--st)", opacity: 0.45 }}>
              {i < idx ? <Check className="h-3 w-3" /> : i + 1}
            </div>
            <span className="text-[9px] font-bold whitespace-nowrap" style={{ opacity: i <= idx ? 1 : 0.4 }}>{label}</span>
          </div>
          {i < TRACK.length - 1 && <div className="h-0.5 flex-1 mx-1 mt-2.5 rounded" style={{ backgroundColor: i < idx ? "var(--sp)" : "var(--ss)", opacity: i < idx ? 1 : 0.4 }} />}
        </div>
      ))}
    </div>
  );
};

export default function CustomerAccount({ slugOverride = null }) {
  // Real Estate websites: delegate to the shared RE experience (portal-linked account page)
  const ReDelegate = RESite;
  const { slug: routeSlug } = useParams();
  const slug = slugOverride || routeSlug;
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [token, setToken] = useState(() => getCustomerToken(slug));
  const [me, setMe] = useState(null);
  const [mode, setMode] = useState(params.get("reset") ? "reset" : params.get("mode") === "signup" ? "signup" : "login");
  const [form, setForm] = useState({ name: "", email: params.get("email") || "", phone: "", password: "", confirm_password: "", new_password: "" });
  const [busy, setBusy] = useState(false);
  const [detail, setDetail] = useState(null);
  const [editing, setEditing] = useState(false);
  const [pform, setPform] = useState({ name: "", phone: "", address: "" });
  const navigate = useNavigate();

  useEffect(() => { api.get(`/public/store/${slug}`).then((r) => setData(r.data)).catch(() => {}); }, [slug]);
  const authH = useCallback(() => ({ headers: { Authorization: `Bearer ${getCustomerToken(slug)}` } }), [slug]);

  const loadMe = useCallback(() => {
    if (!getCustomerToken(slug)) return;
    api.get(`/public/store/${slug}/customer/me`, authH())
      .then((r) => { setMe(r.data); setPform({ name: r.data.customer.name || "", phone: r.data.customer.phone || "", address: r.data.customer.address || "" }); })
      .catch(() => { clearCustomerSession(slug); setToken(null); setMe(null); });
  }, [slug, authH]);
  useEffect(() => { if (token) loadMe(); }, [token, loadMe]);

  const store = data?.store;
  const theme = data?.theme;
  // Same source of truth as Storefront/Checkout: per-store overrides win, then theme colors.
  const colors = store?.settings?.colors || theme?.colors || {};
  const vars = themeVars(colors, theme);
  const head = headingFont(theme);
  const setF = (k, v) => setForm({ ...form, [k]: v });

  const finishAuth = (resp) => {
    setCustomerSession(slug, resp.token, resp.customer);
    setToken(resp.token);
    toast.success(`Welcome, ${resp.customer.name.split(" ")[0]}!`);
    // Return to where the customer came from (e.g. checkout), else clean dashboard URL —
    // never bounce back to the login form on Back.
    const next = params.get("next");
    if (next && next.startsWith("/") && !next.startsWith("//")) {
      setParams({}, { replace: true });
      navigate(next, { replace: true });
    } else {
      setParams({}, { replace: true });
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (mode === "signup") {
        const { data: resp } = await api.post(`/public/store/${slug}/customer/register`, form);
        finishAuth(resp);
      } else if (mode === "login") {
        const { data: resp } = await api.post(`/public/store/${slug}/customer/login`, { email: form.email, password: form.password });
        finishAuth(resp);
      } else if (mode === "forgot") {
        const { data: resp } = await api.post(`/public/store/${slug}/customer/forgot-password`, { email: form.email });
        toast.success(resp.message);
        if (resp.dev_reset_link) window.location.href = resp.dev_reset_link;
      } else if (mode === "reset") {
        await api.post(`/public/store/${slug}/customer/reset-password`, { token: params.get("reset"), password: form.new_password });
        toast.success("Password updated — please log in");
        setMode("login");
        setParams({});
      }
    } catch (e2) { toast.error(errMsg(e2)); }
    finally { setBusy(false); }
  };

  const saveProfile = async () => {
    try {
      const { data: c } = await api.patch(`/public/store/${slug}/customer/me`, pform, authH());
      setMe({ ...me, customer: c });
      setEditing(false);
      toast.success("Profile updated");
    } catch (e2) { toast.error(errMsg(e2)); }
  };

  const openDetail = async (oid) => {
    try { const { data: o } = await api.get(`/public/store/${slug}/customer/orders/${oid}`, authH()); setDetail(o); }
    catch (e2) { toast.error(errMsg(e2)); }
  };

  const logout = () => { clearCustomerSession(slug); setToken(null); setMe(null); setDetail(null); toast.success("Logged out"); };

  const inputCls = "w-full h-11 px-4 rounded-full text-sm outline-none border";
  const inputStyle = { borderColor: "var(--ss)", backgroundColor: "var(--sbg)", color: "var(--st)" };
  const btnStyle = { backgroundImage: "linear-gradient(135deg, var(--sp), var(--sa))" };

  if (data?.is_re) return <ReDelegate slugOverride={slug} baseOv={slugOverride ? "" : `/site/${slug}`} />;

  return (
    <div style={{ ...vars, fontFamily: "var(--sbfont)" }} className="min-h-screen bg-[var(--sbg)] text-[var(--st)]" data-testid="customer-account">
      {/* Store-themed chrome — same header/nav/footer as the storefront, all links store-local */}
      {store && (
        <StoreHeader slug={slug} store={store} info={store.business_info || {}} branding={data?.branding || {}}
          theme={data?.theme} categories={data?.categories || []} />
      )}

      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-10 min-h-[50vh]">
        {!token || !me ? (
          <div className="max-w-md mx-auto rounded-lg border p-7 sm:p-9" style={{ borderColor: "var(--ss)", backgroundColor: "var(--ss)" }} data-testid="customer-auth-card">
            {store?.business_info?.logo_url ? (
              <img src={fileUrl(store.business_info.logo_url)} alt={store?.name} className="h-12 max-w-[180px] object-contain mx-auto mb-5" data-testid="auth-store-logo" />
            ) : (
              <div className="h-12 w-12 rounded-full flex items-center justify-center mx-auto mb-5 text-white" style={btnStyle}><UserRound className="h-5 w-5" /></div>
            )}
            <h1 className={`${head} text-2xl text-center tracking-tight`} data-testid="customer-auth-title">
              {mode === "signup" ? "Create your account" : mode === "forgot" ? "Forgot password" : mode === "reset" ? "Set a new password" : "Welcome back"}
            </h1>
            <p className="text-xs text-center mt-2 mb-7" style={{ opacity: 0.6 }}>
              {mode === "signup" ? `Track your ${store?.name || "store"} orders in one place.` : mode === "forgot" ? "We'll generate a secure reset link." : mode === "reset" ? "Choose a new password for your account." : `Log in to view your orders at ${store?.name || "this store"}.`}
            </p>
            <form onSubmit={submit} className="space-y-3" data-testid="customer-auth-form">
              {mode === "signup" && (
                <>
                  <input required data-testid="ca-name" className={inputCls} style={inputStyle} placeholder="Full name" value={form.name} onChange={(e) => setF("name", e.target.value)} />
                  <input required data-testid="ca-phone" className={inputCls} style={inputStyle} placeholder="Phone number" value={form.phone} onChange={(e) => setF("phone", e.target.value)} />
                </>
              )}
              {mode !== "reset" && (
                <input required type="email" data-testid="ca-email" className={inputCls} style={inputStyle} placeholder="Email address" value={form.email} onChange={(e) => setF("email", e.target.value)} />
              )}
              {(mode === "login" || mode === "signup") && (
                <input required type="password" data-testid="ca-password" className={inputCls} style={inputStyle} placeholder="Password" value={form.password} onChange={(e) => setF("password", e.target.value)} />
              )}
              {mode === "signup" && (
                <input required type="password" data-testid="ca-confirm" className={inputCls} style={inputStyle} placeholder="Confirm password" value={form.confirm_password} onChange={(e) => setF("confirm_password", e.target.value)} />
              )}
              {mode === "reset" && (
                <input required type="password" data-testid="ca-new-password" className={inputCls} style={inputStyle} placeholder="New password (min 6 characters)" value={form.new_password} onChange={(e) => setF("new_password", e.target.value)} />
              )}
              <button type="submit" disabled={busy} data-testid="ca-submit"
                className="w-full py-3.5 rounded-full font-extrabold text-sm text-white transition-all active:scale-95 hover:opacity-90" style={btnStyle}>
                {busy ? "Please wait…" : mode === "signup" ? "Create Account" : mode === "forgot" ? "Send Reset Link" : mode === "reset" ? "Update Password" : "Login"}
              </button>
            </form>
            <div className="flex items-center justify-between mt-5 text-xs font-semibold">
              {mode === "login" && (<>
                <button onClick={() => setMode("signup")} data-testid="ca-to-signup" className="hover:underline" style={{ color: "var(--sp)" }}>Create account</button>
                <button onClick={() => setMode("forgot")} data-testid="ca-to-forgot" className="hover:underline" style={{ color: "var(--sp)" }}>Forgot password?</button>
              </>)}
              {(mode === "signup" || mode === "forgot" || mode === "reset") && (
                <button onClick={() => { setMode("login"); setParams({}); }} data-testid="ca-to-login" className="hover:underline mx-auto" style={{ color: "var(--sp)" }}>Back to login</button>
              )}
            </div>
          </div>
        ) : detail ? (
          <div data-testid="customer-order-detail">
            <button onClick={() => setDetail(null)} className="flex items-center gap-2 text-sm font-bold mb-6 hover:opacity-70 transition-opacity" data-testid="back-to-orders">
              <ArrowLeft className="h-4 w-4" /> All orders
            </button>
            <div className="rounded-lg border p-6 sm:p-8" style={{ borderColor: "var(--ss)" }}>
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <h2 className={`${head} text-2xl tracking-tight`} data-testid="order-detail-number">{detail.order_number}</h2>
                  <p className="text-xs mt-1" style={{ opacity: 0.6 }}>Placed {fmtDateTime(detail.created_at)}</p>
                </div>
                <div className="text-right">
                  <span className="text-[10px] font-extrabold uppercase tracking-wider px-3 py-1.5 rounded-full text-white" style={{ backgroundColor: "var(--sp)" }} data-testid="order-detail-status">{detail.status}</span>
                  <p className="text-[11px] font-bold mt-1.5" style={{ opacity: 0.6 }}>Payment: {detail.payment_status} · {detail.payment_method === "cod" ? "Cash on Delivery" : detail.payment_method === "whatsapp" ? "WhatsApp" : "Online"}</p>
                </div>
              </div>
              <TrackBar status={detail.status} />
              <div className="mt-7 space-y-3">
                {detail.items.map((it, i) => (
                  <div key={i} className="flex items-center gap-4 rounded-md border p-3" style={{ borderColor: "var(--ss)" }} data-testid={`order-item-${i}`}>
                    <SafeImg src={it.image} alt={it.name} className="h-14 w-14 rounded-md object-cover" />
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-bold truncate">{it.name}</p>
                      <p className="text-xs" style={{ opacity: 0.6 }}>{it.variation ? `${it.variation} · ` : ""}Qty {it.qty} × {fmtINR(it.price)}</p>
                    </div>
                    <p className="text-sm font-extrabold">{fmtINR(it.price * it.qty)}</p>
                  </div>
                ))}
              </div>
              <div className="mt-6 pt-4 space-y-1.5 text-sm border-t" style={{ borderColor: "var(--ss)" }}>
                <div className="flex justify-between"><span style={{ opacity: 0.6 }}>Subtotal</span><span className="font-bold">{fmtINR(detail.subtotal)}</span></div>
                <div className="flex justify-between"><span style={{ opacity: 0.6 }}>Shipping</span><span className="font-bold">{detail.shipping ? fmtINR(detail.shipping) : "Free"}</span></div>
                {detail.discount > 0 && <div className="flex justify-between"><span style={{ opacity: 0.6 }}>Discount{detail.coupon ? ` (${detail.coupon})` : ""}</span><span className="font-bold">−{fmtINR(detail.discount)}</span></div>}
                {detail.tax > 0 && <div className="flex justify-between"><span style={{ opacity: 0.6 }}>Tax</span><span className="font-bold">{fmtINR(detail.tax)}</span></div>}
                <div className="flex justify-between text-base pt-2 border-t" style={{ borderColor: "var(--ss)" }}><span className="font-extrabold">Total</span><span className="font-extrabold" data-testid="order-detail-total">{fmtINR(detail.total)}</span></div>
              </div>
              {detail.customer?.address && (
                <div className="mt-5 rounded-md p-4 text-xs leading-relaxed" style={{ backgroundColor: "var(--ss)" }}>
                  <p className="font-extrabold mb-1">Delivery address</p>
                  <p style={{ opacity: 0.75 }}>{detail.customer.name} · {detail.customer.phone}<br />{detail.customer.address}</p>
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="space-y-6">
            <div className="rounded-lg border p-6" style={{ borderColor: "var(--ss)" }} data-testid="customer-profile">
              <div className="flex items-center justify-between mb-4">
                <h2 className={`${head} text-xl tracking-tight flex items-center gap-2`}><UserRound className="h-5 w-5" /> My Profile</h2>
                <div className="flex items-center gap-3">
                  {!editing ? (
                    <button onClick={() => setEditing(true)} data-testid="edit-profile-button" className="flex items-center gap-1.5 text-xs font-bold hover:underline" style={{ color: "var(--sp)" }}><Pencil className="h-3.5 w-3.5" /> Edit</button>
                  ) : (
                    <div className="flex gap-2">
                      <button onClick={saveProfile} data-testid="save-profile-button" className="text-xs font-extrabold text-white px-4 py-2 rounded-full" style={btnStyle}>Save</button>
                      <button onClick={() => setEditing(false)} className="h-8 w-8 rounded-full border flex items-center justify-center" style={{ borderColor: "var(--ss)" }}><X className="h-3.5 w-3.5" /></button>
                    </div>
                  )}
                  <button onClick={logout} data-testid="customer-logout" className="flex items-center gap-1.5 text-xs font-bold hover:underline" style={{ color: "var(--sp)" }}><LogOut className="h-3.5 w-3.5" /> Logout</button>
                </div>
              </div>
              {editing ? (
                <div className="space-y-3">
                  <input className={inputCls} style={inputStyle} data-testid="profile-name-input" placeholder="Full name" value={pform.name} onChange={(e) => setPform({ ...pform, name: e.target.value })} />
                  <input className={inputCls} style={inputStyle} data-testid="profile-phone-input" placeholder="Phone" value={pform.phone} onChange={(e) => setPform({ ...pform, phone: e.target.value })} />
                  <input className={inputCls} style={inputStyle} data-testid="profile-address-input" placeholder="Saved delivery address" value={pform.address} onChange={(e) => setPform({ ...pform, address: e.target.value })} />
                </div>
              ) : (
                <div className="grid sm:grid-cols-2 gap-3 text-sm">
                  <div><p className="text-[10px] font-extrabold uppercase tracking-widest" style={{ opacity: 0.45 }}>Name</p><p className="font-bold mt-0.5" data-testid="profile-name">{me.customer.name}</p></div>
                  <div><p className="text-[10px] font-extrabold uppercase tracking-widest" style={{ opacity: 0.45 }}>Email</p><p className="font-bold mt-0.5" data-testid="profile-email">{me.customer.email}</p></div>
                  <div><p className="text-[10px] font-extrabold uppercase tracking-widest" style={{ opacity: 0.45 }}>Phone</p><p className="font-bold mt-0.5" data-testid="profile-phone">{me.customer.phone}</p></div>
                  <div><p className="text-[10px] font-extrabold uppercase tracking-widest" style={{ opacity: 0.45 }}>Saved Address</p><p className="font-bold mt-0.5" data-testid="profile-address">{me.customer.address || "—"}</p></div>
                </div>
              )}
            </div>

            <div className="rounded-lg border p-6" style={{ borderColor: "var(--ss)" }} data-testid="customer-orders">
              <h2 className={`${head} text-xl tracking-tight flex items-center gap-2 mb-4`}><Package className="h-5 w-5" /> My Orders ({me.orders.length})</h2>
              {me.orders.length === 0 ? (
                <p className="text-sm" style={{ opacity: 0.6 }}>No orders yet. <Link to={storePath(slug, "")} className="font-bold hover:underline" style={{ color: "var(--sp)" }}>Start shopping →</Link></p>
              ) : (
                <div className="space-y-3">
                  {me.orders.map((o) => (
                    <button key={o.id} onClick={() => openDetail(o.id)} data-testid={`order-row-${o.order_number}`}
                      className="w-full flex items-center gap-4 rounded-md border p-4 text-left transition-all hover:shadow-md" style={{ borderColor: "var(--ss)" }}>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-extrabold">{o.order_number}</p>
                        <p className="text-xs mt-0.5" style={{ opacity: 0.6 }}>{fmtDateTime(o.created_at)} · {o.items.reduce((n, i) => n + i.qty, 0)} item(s)</p>
                      </div>
                      <div className="text-right shrink-0">
                        <p className="text-sm font-extrabold">{fmtINR(o.total)}</p>
                        <p className="text-[10px] font-extrabold uppercase tracking-wider mt-0.5" style={{ color: "var(--sp)" }}>{o.status}</p>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </main>

      {store && (
        <StoreFooter slug={slug} store={store} info={store.business_info || {}} branding={data?.branding || {}}
          theme={data?.theme} categories={data?.categories || []} />
      )}
    </div>
  );
}
